"use client";

import { useEffect, useRef, useState } from "react";
import { AspectRatio } from "@repo/ui/aspect-ratio";
import { Button } from "@repo/ui/button";
import { CloudinaryImage } from "@repo/ui/cloudinary-image";
import * as Grid from "@repo/ui/grid";
import * as List from "@repo/ui/list";
import { VideoPlayer } from "@repo/ui/video-player";
import { Zoomable } from "@repo/ui/zoomable";
import { ImageSizes } from "@repo/utils/image-presets";
import classes from "./gallery.module.css";

type ScrollIndicatorsProps = {
  totalImages: number;
  activeIndex: number;
  axis: "x" | "y";
  position: "start" | "end";
};

const ScrollIndicators = ({
  totalImages,
  activeIndex,
  axis,
  position,
}: ScrollIndicatorsProps) => {
  const indicatorClasses = [
    classes.scrollIndicators,
    classes[`scrollIndicators-${axis}`],
    classes[`scrollIndicators-${position}`],
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <ul
      className={`${indicatorClasses} ${classes.scrollIndicatorsList}`}
      aria-hidden="true"
    >
      {Array.from({ length: totalImages }, (_, index) => (
        <li
          key={index}
          className={`${classes.scrollIndicator} ${
            index === activeIndex ? classes["scrollIndicator-active"] : ""
          }`}
          aria-current={index === activeIndex ? "true" : undefined}
        />
      ))}
    </ul>
  );
};

export type GalleryProps = {
  productImages: string[];
  productName: string;
  videoUrl?: string;
  autoPlayVideos?: boolean;
  sourceSetProfile: ImageSizes;
  aspectRatio?: number | "auto";
  itemAspectRatio: number;
  sourceSetSizes: string;
  scrollDirection?: "vertical" | "horizontal";
  onImageClick?: (index: number) => void;
  zoomImages?: boolean;
  columns?: number;
  showScrollIndicators?: boolean;
  indicatorAxis?: "x" | "y";
  indicatorPosition?: "start" | "end";
};

export const Gallery = ({
  productImages,
  productName,
  videoUrl,
  sourceSetProfile,
  sourceSetSizes,
  aspectRatio,
  itemAspectRatio,
  scrollDirection = "vertical",
  onImageClick,
  zoomImages = true,
  autoPlayVideos = false,
  columns = 1,
  showScrollIndicators = false,
  indicatorAxis = "y",
  indicatorPosition = "start",
}: GalleryProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const productImagesWithVideo =
    videoUrl && videoUrl !== ""
      ? [...productImages.slice(0, 1), "video", ...productImages.slice(1)]
      : productImages;

  useEffect(() => {
    if (!showScrollIndicators || !listRef.current) {
      return;
    }

    const listElement = listRef.current;

    const updateActiveIndex = () => {
      const children = listElement.children;
      if (children.length <= 1) return;

      const scrollPosition =
        scrollDirection === "horizontal"
          ? listElement.scrollLeft
          : listElement.scrollTop;

      const firstChild = children[0];
      if (!firstChild) return;

      const itemSize =
        scrollDirection === "horizontal"
          ? firstChild.clientWidth
          : firstChild.clientHeight;

      const newIndex = Math.round(scrollPosition / itemSize);
      const clampedIndex = Math.max(
        0,
        Math.min(newIndex, productImagesWithVideo.length - 1),
      );

      setActiveIndex(clampedIndex);
    };

    listElement.addEventListener("scroll", updateActiveIndex);
    listElement.addEventListener("scrollend", updateActiveIndex);

    return () => {
      listElement.removeEventListener("scroll", updateActiveIndex);
      listElement.removeEventListener("scrollend", updateActiveIndex);
    };
  }, [showScrollIndicators, scrollDirection, productImagesWithVideo.length]);

  const listContent = (
    <List.Root
      ref={listRef}
      snap
      className={classes.galleryList}
      direction={scrollDirection}
      data-scroll-direction={scrollDirection}
    >
      {productImagesWithVideo.map((productImage, i) => {
        const imageTemplate = (
          <CloudinaryImage
            alt={`Model wearing ${productName}`}
            loading="eager"
            url={productImage}
            srcSize={240}
            sourceSetProfile={sourceSetProfile}
            sizes={sourceSetSizes}
          />
        );

        const image = (
          <AspectRatio ratio={itemAspectRatio}>
            {onImageClick ? (
              <Button
                onClick={() => {
                  if (onImageClick) {
                    onImageClick(i);
                  }
                }}
                className={classes.imageButton}
              >
                {imageTemplate}
              </Button>
            ) : (
              imageTemplate
            )}
          </AspectRatio>
        );

        const zoomableImage = <Zoomable>{image}</Zoomable>;

        const content =
          productImage === "video" && videoUrl ? (
            <AspectRatio ratio={itemAspectRatio}>
              <VideoPlayer
                src={videoUrl}
                controls
                autoPlay={autoPlayVideos}
                loop={false}
                muted
                description={`Video showing model wearing ${productName}`}
                className={classes.videoPlayer}
              />
            </AspectRatio>
          ) : zoomImages ? (
            zoomableImage
          ) : (
            image
          );

        return columns > 1 && scrollDirection !== "horizontal" ? (
          <Grid.Item key={`productImage-${i}`} asChild>
            <List.Item>{content}</List.Item>
          </Grid.Item>
        ) : (
          <List.Item key={`productImage-${i}`}>{content}</List.Item>
        );
      })}
    </List.Root>
  );

  const galleryList =
    columns > 1 && scrollDirection !== "horizontal" ? (
      <Grid.Container columns={columns} asChild>
        {listContent}
      </Grid.Container>
    ) : (
      listContent
    );

  const galleryWithIndicators = showScrollIndicators ? (
    <div
      className={classes.galleryWrapper}
      {...(scrollDirection === "horizontal"
        ? { "data-horizontal-scroll": "" }
        : {})}
    >
      {showScrollIndicators && productImagesWithVideo.length > 1 && (
        <ScrollIndicators
          totalImages={productImagesWithVideo.length}
          activeIndex={activeIndex}
          axis={indicatorAxis}
          position={indicatorPosition}
        />
      )}
      {galleryList}
    </div>
  ) : (
    galleryList
  );

  return aspectRatio ? (
    <AspectRatio ratio={aspectRatio}>{galleryWithIndicators}</AspectRatio>
  ) : (
    galleryWithIndicators
  );
};
